import React, { CSSProperties } from 'react';
import {
  ComposerPrimitive,
  useAssistantApi,
  useAssistantState,
} from '@assistant-ui/react';
import clsx from 'clsx';

import { Icon, IconButton } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { ACCEPTED_ATTACHMENT_TYPES_INPUT } from '@atom/utilities/aiChatUtilities';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { showDevelopmentFeature } from '@atom/utilities/featureToggleUtilities';

import ChatAttachmentPreview from './ChatAttachmentPreview';
import { useChatContext } from './ChatContext';
import ChatImpersonation from './ChatImpersonation';
import ChatModelSelection from './ChatModelSelection';

import classes from './chat.module.css';

const styles: Record<string, CSSProperties> = {
  icon: {
    fontSize: fonts.xxxl,
    color: colors.neutral.white,
  },
  attachIcon: {
    fontSize: fonts.md,
    color: colors.neutral.gray,
  },
};

const ChatInput = () => {
  const isRunning = useAssistantState(state => state.thread.isRunning);
  const composer = useAssistantState(state => state.composer);
  const api = useAssistantApi();
  const runtime = api.thread();
  const {
    attachmentsData: {
      stageAttachments,
      stagedAttachments,
      removeStagedAttachment,
      isUploading,
    },
  } = useChatContext();

  const hasValue = composer.text?.trim() !== '';
  const isAdmin = hasRolePermissions(ROLE_SETS.ADMIN);
  const canSeeToolbar = isAdmin && showDevelopmentFeature;
  const canUseAttachments = isAdmin && showDevelopmentFeature;
  const canImpersonate = isAdmin && showDevelopmentFeature;
  const canSelectModel = isAdmin && showDevelopmentFeature;
  const canAttach = canUseAttachments && stagedAttachments.length < 5;
  const canSend = hasValue && !isUploading;

  const handleClick = () => {
    if (isRunning) {
      runtime.cancelRun();
    }
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (files && files.length > 0) {
      stageAttachments(files);
    }

    event.target.value = '';
  };

  return (
    <ComposerPrimitive.Root className={classes.inputRootContainer}>
      {canUseAttachments && (
        <input
          id="chat-attach-input"
          style={{ display: 'none' }}
          type="file"
          multiple
          accept={ACCEPTED_ATTACHMENT_TYPES_INPUT}
          onChange={handleFilesSelected}
        />
      )}
      {stagedAttachments.length > 0 && (
        <div className={classes.attachmentPreviews}>
          {stagedAttachments.map(attachment => (
            <ChatAttachmentPreview
              key={attachment.mediaId}
              attachment={attachment}
              onRemove={() => removeStagedAttachment(attachment.mediaId)}
            />
          ))}
        </div>
      )}
      <div className={classes.inputWrapper}>
        <div className={classes.inputTopArea}>
          <ComposerPrimitive.Input
            placeholder="Ask Atom AI a question"
            className={classes.inputField}
            rows={1}
          />
          <button
            onClick={handleClick}
            className={clsx(
              classes.inputSendButton,
              isRunning || canSend
                ? classes.inputSendButtonActive
                : classes.inputSendButtonInactive,
            )}
            disabled={!isRunning && !canSend}
          >
            <Icon style={styles.icon}>
              {isRunning ? 'stop' : 'arrow_upward'}
            </Icon>
          </button>
        </div>
        {canSeeToolbar && (
          <div className={classes.inputBottomToolbar}>
            {canSelectModel && <ChatModelSelection />}
            {canUseAttachments && (
              <label
                htmlFor="chat-attach-input"
                className={clsx(
                  classes.attachButton,
                  !canAttach && classes.attachButtonDisabled,
                )}
              >
                <IconButton
                  component="span"
                  disabled={!canAttach}
                  size="small"
                  tooltip="Add photos"
                >
                  <Icon style={styles.attachIcon}>add_photo_alternate</Icon>
                </IconButton>
              </label>
            )}
            {canImpersonate && <ChatImpersonation />}
          </div>
        )}
      </div>
      <div className={classes.inputHelperText}>
        Atom AI can make mistakes. Please verify any important information.
      </div>
    </ComposerPrimitive.Root>
  );
};

export default ChatInput;
